<template>
<div
    class="ql-cue-layer-picker-content cue-header-layer-title"
>
    <div v-if="locked || !editable" class="ql-button-container">
        <!-- Rendered as a plain div (not md-button) so Safari's PDF export doesn't
             drop the layer name — WebKit omits <button> content in print/export.
             This branch is the locked/print view, so the button was non-interactive. -->
        <div class="ql-cue-layer-picker-button ql-cue-layer-picker-static" :style="layerTitleStyle()">
            <md-icon :style="layerTitleIconStyle()">more_vert</md-icon><span v-show="headerBox.layerName !== false">{{currentLayer.title}}</span>
        </div>
    </div>
    <md-menu v-else md-size="4" ref="layerMenu" :md-close-on-select="false" md-align-trigger :md-offset-x="menuOffsetStyleX()" :md-offset-y="menuOffsetStyleY()" @open="resetPageDraft" @close="resetPageDraft">
        <md-button md-menu-trigger class="ql-cue-layer-picker-button" :style="layerTitleStyle()">
            <md-icon :style="layerTitleIconStyle()">more_vert</md-icon><span v-show="headerBox.layerName !== false">{{currentLayer.title}}</span>
        </md-button>
        <md-menu-content class="ql-cue-menu-content">
            <md-menu-item disabled><span>Change Layer</span></md-menu-item>
            <md-menu-item v-for="layer in layers" :key="layer.id" @selected="selectLayer(layer.id)" class="ql-cue-layer-picker-wrapper">
                <span class="ql-cue-layer-picker" :style="style(layer)"></span>
                <span class="ql-cue-layer-picker-option">{{layer.title}}</span>
            </md-menu-item>
            <md-menu-item disabled>
                <div style="border-top: 1px solid #ddd; margin: 8px 0;"></div>
            </md-menu-item>
            <md-menu-item>
                <div class="md-list-item-container">
                    <div class="md-list-item-content">
                        <span>Cue Size:</span>
                        <vue-slider v-model="cueTitleFontSize" :min="8" :max="24" @change="changeTitleFontSize"></vue-slider>
                    </div>
                </div>
            </md-menu-item>
            <md-menu-item>
                <div class="md-list-item-container">
                    <div class="md-list-item-content">
                        <span>Cue Line Opacity:</span>
                        <vue-slider v-model="cueLineOpacity" @change="changeLineOpacity"></vue-slider>
                    </div>
                </div>
            </md-menu-item>
            <md-menu-item @selected="restoreDefault()">
                <span style="color: #5ecfdf;">Restore Default</span>
            </md-menu-item>
            <md-menu-item disabled>
                <div style="border-top: 1px solid #ddd; margin: 8px 0;"></div>
            </md-menu-item>
            <md-menu-item>
                <div class="md-list-item-container">
                    <div class="md-list-item-content ql-cue-menu-list-item-content">
                        <span>Cue Marker:</span>
                        <div class="md-list-item-radio-options">
                            <div><md-radio v-model="cue.type" mdValue="position" class="md-primary" @change="changeCueMarker"> Line</md-radio></div>
                            <div><md-radio v-model="cue.type" mdValue="selection" class="md-primary" @change="changeCueMarker"> Resizable Box</md-radio></div>
                        </div>
                        <div v-if="cue.type === 'selection'">
                            <span>Cue Box Opacity:</span>
                            <vue-slider v-model="cueBoxOpacity" @change="changeBoxOpacity"></vue-slider>
                        </div>
                    </div>
                </div>
            </md-menu-item>
            <md-menu-item disabled>
                <div style="border-top: 1px solid #ddd; margin: 8px 0;"></div>
            </md-menu-item>
            <md-menu-item>
                <div class="md-list-item-container">
                    <div class="md-list-item-content ql-cue-menu-list-item-content">
                        <span>Visible in cue:</span>
                        <div class="md-list-item-radio-options">
                            <div class="headerBoxOption" :class="{'headerBoxOptionMarked': headerBox.title}" @click="changeHeaderBoxTitle">
                                <md-icon v-show="!headerBox.title">check_box_outline_blank</md-icon>
                                <md-icon v-show="headerBox.title">check_box</md-icon>
                                <span>Number/Title</span>
                            </div>
                            <div class="headerBoxOption" :class="{'headerBoxOptionMarked': headerBox.label}" @click="changeHeaderBoxLabel">
                                <md-icon v-show="!headerBox.label">check_box_outline_blank</md-icon>
                                <md-icon v-show="headerBox.label">check_box</md-icon>
                                <span>Label</span>
                            </div>
                            <div class="headerBoxOption" :class="{'headerBoxOptionMarked': headerBox.time}" @click="changeHeaderBoxTime">
                                <md-icon v-show="!headerBox.time">check_box_outline_blank</md-icon>
                                <md-icon v-show="headerBox.time">check_box</md-icon>
                                <span>Time</span>
                            </div>
                        </div>
                    </div>
                </div>
            </md-menu-item>
            <md-menu-item disabled><span>Change Page:</span></md-menu-item>
            <md-menu-item>
                <div class="md-list-item-container">
                    <div class="md-list-item-content" style="flex-wrap: wrap;">
                        <div style="display: flex; align-items: center;">
                            <md-button class="md-icon-button md-raised md-dense" v-bind:disabled="!canPageDown" v-on:click="pageDown"><md-icon>keyboard_arrow_left</md-icon></md-button>
                            <div class="page-number-container">
                                <input type="text" ref="input_page_number" v-model="pageDraft" class="input_page_number" @keyup.enter="change_page" @input="pageError = ''" v-on:keydown.left="ignoreKeydown" v-on:keydown.right="ignoreKeydown" />
                                    / {{ lastPageLabel }}
                            </div>
                            <md-button class="md-icon-button md-raised md-dense" v-bind:disabled="!canPageUp" v-on:click="pageUp"><md-icon>keyboard_arrow_right</md-icon></md-button>
                        </div>
                        <span class="page-number-error" v-if="pageError">{{ pageError }}</span>
                    </div>
                </div>
            </md-menu-item>
            <md-menu-item @selected="deleteCueMarker()">
                <md-icon>delete</md-icon><span>Delete Cue</span>
            </md-menu-item>
        </md-menu-content>
    </md-menu>
</div>
</template>

<script>
import VueSlider from 'vue-slider-component';

import { mapGetters } from 'vuex';
import { resolvePageLabel, labelForPage, isValidPageNumber } from '@/lib/cue-page';

const ProjectCuesLayerPicker = {
    name: 'projectCuesLayerPicker',
    props: ['cue', 'currentLayer', 'editable', 'locked', 'titleFontSize', 'headerBox', 'userDefined', 'layerCueLineOpacity', 'layerCueBoxOpacity'],
    data: function() {
        return {
            move_cue_to: Number(this.$props.cue.pageNumber),
            // The page box holds a PRINTED label, edited freely and resolved only on Enter.
            pageDraft: '',
            pageError: '',
            cueTitleFontSize: this.$props.titleFontSize,
            cueLineOpacity: this.$props.layerCueLineOpacity,
            cueBoxOpacity: this.$props.layerCueBoxOpacity,
            changeTitleFontSizeTimer: null,
            changeLineOpacityTimer: null,
            changeBoxOpacityTimer: null
        };
    },
    computed: {
        ...mapGetters({
            selectedScale: localStorage.getItem('page-print-scale') || 1,
            userProjectData: 'userProjectData',
            layers: 'layers',
            project: 'project',
            getCueLineOpacity: 'getCueLineOpacity',
            currentVersion: 'currentVersion',
            projectTotalPages: 'projectTotalPages'
        }),
        canPageDown() {
            return this.move_cue_to > 1;
        },
        canPageUp() {
            return this.move_cue_to < this.totalPages;
        },
        versionID: function() {
            return this.currentVersion ? this.currentVersion.id : '';
        },
        pageLabels: function() {
            const source = this.versionID ? this.currentVersion : this.project;
            return (source && Array.isArray(source.pdfPageNumbers)) ? source.pdfPageNumbers : [];
        },
        lastPageLabel: function() {
            return labelForPage(this.pageLabels, this.pageLabels.length);
        },
        totalPages: function() {
            if (this.versionID) {
                return this.projectTotalPages || 0;
            }
            
            return (this.project && Array.isArray(this.project.pdfPageNumbers)) 
                ? this.project.pdfPageNumbers.length 
                : 0;
        }
    },
    watch: {
        'cue.pageNumber': function() {
            this.resetPageDraft();
        },
        'cue.titleFontSize': function() {
            this.prefillCueTitleFontSize();
        },
        titleFontSize: function() {
            this.prefillCueTitleFontSize();
        },
        'cue.lineOpacity': function() {
            this.prefillCueLineOpacity();
        },
        'currentLayer.lineOpacity': function() {
            this.prefillCueLineOpacity();
        },
        'cue.boxOpacity': function() {
            this.prefillCueBoxOpacity();
        },
        'currentLayer.boxOpacity': function() {
            this.prefillCueBoxOpacity();
        }
    },
    methods: {
        style(layer) {
            const val = 'background-color: ' + layer.color + ' !important;';
            return val;
        },
        open() {
            this.$refs.layerMenu.open();
        },
        close() {
            this.$refs.layerMenu.close();
        },
        selectLayer(layer) {
            this.$emit('selectLayer', layer);
        },
        ignoreKeydown(e) {
            e.stopPropagation();
        },
        resetPageDraft() {
            this.move_cue_to = Number(this.cue.pageNumber);
            this.pageDraft = labelForPage(this.pageLabels, this.move_cue_to) || String(this.cue.pageNumber || '');
            this.pageError = '';
        },
        // Enter: the draft is a printed label (see lib/cue-page.js). Not a label -> message, no write.
        change_page() {
            const draft = String(this.pageDraft == null ? '' : this.pageDraft).trim();
            const target = resolvePageLabel(draft, this.pageLabels);
            if (!target || !isValidPageNumber(target, this.totalPages || undefined)) {
                this.pageError = `No page "${draft}" in this production.`;
                return;
            }
            this.moveCueTo(target);
        },
        // The arrows move the cue straight away in this view (unchanged behaviour).
        moveCueTo(target) {
            this.pageError = '';
            this.move_cue_to = target;
            this.pageDraft = labelForPage(this.pageLabels, target);
            if (target !== Number(this.cue.pageNumber)) {
                this.cue.pageNumber = target;
                this.$store.dispatch('updateCue', this.cue).catch(e => console.error(e));
            }
        },
        changeCueMarker() {
            this.cue.type = this.cue.type === 'position' ? 'selection' : 'position';
            if (this.cue.type === 'position') {
                this.cue.width = 8;
                this.cue.height = 8;
            } else {
                this.cue.width = 60;
                this.cue.height = 20;
            }
            this.$store.dispatch('updateCue', this.cue);
        },
        prefillCueTitleFontSize() {
            this.cueTitleFontSize = this.$props.titleFontSize;
        },
        prefillCueLineOpacity() {
            this.cueLineOpacity = 30;
            if (this.$props.currentLayer.lineOpacity !== undefined) {
                this.cueLineOpacity = this.$props.currentLayer.lineOpacity;
            }
            if (this.$props.cue.lineOpacity !== undefined) {
                this.cueLineOpacity = this.$props.cue.lineOpacity;
            }
        },
        prefillCueBoxOpacity() {
            this.cueBoxOpacity = 20;
            if (this.$props.currentLayer.boxOpacity !== undefined) {
                this.cueBoxOpacity = this.$props.currentLayer.boxOpacity;
            }
            if (this.$props.cue.boxOpacity !== undefined) {
                this.cueBoxOpacity = this.$props.cue.boxOpacity;
            }
        },
        changeTitleFontSize() {
            if (this.changeTitleFontSizeTimer) {
                clearTimeout(this.changeTitleFontSizeTimer);
            }
            this.changeTitleFontSizeTimer = setTimeout(() => {
                this.$store.dispatch('addCueTitleFontSize', {
                    id: this.cue.id,
                    titleFontSize: this.cueTitleFontSize
                });
                this.changeTitleFontSizeTimer = null;
            }, 500);
        },
        getTitleFontSize(layer) {
            const layers = this.userProjectData.layers;
            if (layers && layers[layer.id]) {
                if (layers[layer.id].titleFontSize) {
                    return layers[layer.id].titleFontSize;
                }
            }
            return 14;
        },
        changeLineOpacity() {
            if (this.changeLineOpacityTimer) {
                clearTimeout(this.changeLineOpacityTimer);
            }
            this.changeLineOpacityTimer = setTimeout(() => {
                this.cue.lineOpacity = this.cueLineOpacity;
                this.$store.dispatch('updateCue', this.cue);
                this.changeLineOpacityTimer = null;
            }, 500);
        },
        restoreDefault() {
            this.cueTitleFontSize = this.getTitleFontSize({id: this.cue.layer});
            this.cueLineOpacity = this.getCueLineOpacity(this.cue);
            delete this.cue.titleFontSize;
            delete this.cue.lineOpacity;
            this.$store.dispatch('removeCueProperties', {
                cue: this.cue,
                keys: ['titleFontSize', 'lineOpacity']
            });
        },
        changeBoxOpacity() {
            if (this.changeBoxOpacityTimer) {
                clearTimeout(this.changeBoxOpacityTimer);
            }
            this.changeBoxOpacityTimer = setTimeout(() => {
                this.cue.boxOpacity = this.cueBoxOpacity;
                this.$store.dispatch('updateCue', this.cue);
                this.changeBoxOpacityTimer = null;
            }, 500);
        },
        deleteCueMarker() {
            this.$store.dispatch('deleteCue', this.cue);
        },
        pageDown() {
            if (this.canPageDown) {
                this.moveCueTo(this.move_cue_to - 1);
            }
        },
        pageUp() {
            if (this.canPageUp) {
                this.moveCueTo(this.move_cue_to + 1);
            }
        },
        changeHeaderBoxTitle() {
            if (this.headerBox.title && !this.headerBox.label) return;
            // Pin ONLY title for this cue; label/time keep following the global default.
            this.$set(this.headerBox, 'title', !this.headerBox.title);
            this.$store.dispatch('addCueHeaderBoxOptions', {
                id: this.cue.id,
                title: this.headerBox.title
            });
        },
        changeHeaderBoxLabel() {
            if (!this.headerBox.title && this.headerBox.label) return;
            this.$set(this.headerBox, 'label', !this.headerBox.label);
            this.$store.dispatch('addCueHeaderBoxOptions', {
                id: this.cue.id,
                label: this.headerBox.label
            });
        },
        changeHeaderBoxTime() {
            // Time is independent — no "at least one" guard; it can be freely toggled on/off.
            this.$set(this.headerBox, 'time', !this.headerBox.time);
            this.$store.dispatch('addCueHeaderBoxOptions', {
                id: this.cue.id,
                time: this.headerBox.time
            });
        },
        getCurrentCueSize() {
            return this.$props.titleFontSize;
        },
        layerTitleStyle() {
            return {
                fontSize: this.getCurrentCueSize() + 'px'
            };
        },
        layerTitleIconStyle() {
            const cueSize = this.getCurrentCueSize();
            return {
                fontSize: cueSize + 'px',
                width: (cueSize / 2) + 'px',
                height: cueSize + 'px',
                minWidth: (cueSize / 2) + 'px',
                minHeight: cueSize + 'px',
                marginLeft: (-cueSize / 4) + 'px'
            };
        },
        menuOffsetStyleX() {
            return -16 * this.selectedScale;
        },
        menuOffsetStyleY() {
            return 2;
        }
    },
    components: {
        'vue-slider': VueSlider
    },
    created: function() {
        this.cueLineOpacity = 30;
        if (this.$props.currentLayer.lineOpacity !== undefined) {
            this.cueLineOpacity = this.$props.currentLayer.lineOpacity;
        }
        if (this.$props.cue.lineOpacity !== undefined) {
            this.cueLineOpacity = this.$props.cue.lineOpacity;
        }

        this.cueBoxOpacity = 20;
        if (this.$props.currentLayer.boxOpacity !== undefined) {
            this.cueBoxOpacity = this.$props.currentLayer.boxOpacity;
        }
        if (this.$props.cue.boxOpacity !== undefined) {
            this.cueBoxOpacity = this.$props.cue.boxOpacity;
        }
    }
};

export default ProjectCuesLayerPicker;
</script>

<style lang="scss">
.app-print-mode {
    .ql-cue-layer-picker-content {
        display: flex;
        > .md-menu {
            display: flex;
        }
    }
    .ql-cue-layer-picker {
        min-height: 32px; 
        min-width: 32px;
        margin: 0;
        border-radius: 0;
        .ql-cue-layer-picker {
            min-width: 32px;
            min-height: 32px;
        }
    }
    .ql-cue-layer-picker-button {
        margin: 0;
        padding: 0;
        min-width: 0;
        line-height: 100%;
        min-height: 0;
        display: flex;
        align-items: center;
        .md-icon {
            color: #999;
            margin-right: 4px;
        }
        span {
            line-height: 1.1;
            display: block;
            display: inline-block;
            width: auto;
            max-width: 130px;
            white-space: nowrap;
            overflow: hidden !important;
            text-overflow: ellipsis;
        }
    }
    .ql-cue-layer-picker-option {
        margin-left: 12px;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .ql-cue-layer-picker-wrapper {
        .ql-cue-layer-picker {
            min-width: 32px;
            min-height: 32px;
        }
    }
    .input_page_number {
        width: 40px;
        text-align: center;
    }
    .page-number-error {
        display: block;
        flex-basis: 100%;
        margin-top: 4px;
        color: #c0392b;
        font-size: 12px;
        line-height: 1.3;
        white-space: normal;
    }
    .headerBoxOption {
        &.headerBoxOptionMarked {
            i {
                color: #5ecfdf !important;
            }
        }
    }
    .md-list-item-content {
        flex: 1;
    }
    .ql-cue-menu-content {
        width: auto;
        max-width: -webkit-fill-available;
    }
    .ql-cue-menu-list-item-content {
        .md-list-item-radio-options {
            margin-top: 12px;
            margin-bottom: 8px;
        }
        .md-radio {
            margin: 0 0 8px;
        }
    }
}
</style>


// WEBPACK FOOTER //
// layer-picker.vue?54ff72d1